import { SchemaEngine, ColumnType } from '../../../core/schema-engine';
import { isConversionError } from '../../../core/type-converter';
import { DialogStore } from '../../stores/DialogStore';
import { AppStore } from '../../stores/AppStore';
import * as HelperHandlers from './helper-handlers';
import * as NotificationHandlers from './notification-handlers';
import { StepService } from '../../services/StepService';
import { PersistenceService } from '../../services/PersistenceService';
import { convertType } from '../../../core/type-converter';
import { NameService } from '../../services/NameService';
import i18n from '../../../i18n';

export function handleBodyClick(event: any) {
  // Selection-safe UI surfaces use stopPropagation() so their clicks never reach here.
  // The data-attribute checks below are belt-and-suspenders for elements that also stopPropagation.
  const hasSelection =
    AppStore.selectedColumn.value ||
    AppStore.selectedColumns.value.length > 0 ||
    AppStore.selectedRows.value.length > 0;
  if (
    hasSelection &&
    !event.target.closest('[data-row-gutter]') &&
    !event.target.closest('[data-eda-panel="true"]')
  ) {
    clearColumnSelection();
  }
  if (
    AppStore.typeMenuOpen.value &&
    !event.target.closest('.type-menu') &&
    !event.target.closest('.type-indicator')
  ) {
    AppStore.typeMenuOpen.value = false;
    AppStore.typeMenuCol.value = null;
    // Clear preview when type menu closes
    DialogStore.previewState.title.value = '';
    DialogStore.previewState.stats.value = '';
    DialogStore.previewState.columns.value = [];
    DialogStore.previewState.newColumns.value = [];
    DialogStore.previewState.rows.value = [];
  }
}

export function openColumnMenu(col: string, _event?: Event) {
  // Select the column so quick actions (quickSort, quickFilter, etc.) can read it
  AppStore.selectedColumn.value = col;
  AppStore.selectedColumns.value = [col];
  AppStore.columnSelectionAnchor.value = col;
  AppStore.selectedRows.value = [];
  AppStore.rowSelectionAnchor.value = null;
  AppStore.typeMenuOpen.value = false;
  AppStore.selectedCell.value = null;

  // Position menu below the column header
  const header = document.querySelector(`th[data-col="${col}"]`);
  if (!header) return;
  const rect = header.getBoundingClientRect();
  const menuWidth = 180;
  const margin = 8;
  const windowWidth = window.innerWidth;
  const x = Math.min(rect.left, windowWidth - menuWidth - margin);
  AppStore.columnMenuOpen.value = col;
  AppStore.columnMenuPos.value = { x, y: rect.bottom + 4 };
}

export function openTypeMenu(col: string, event: any) {
  AppStore.typeMenuCol.value = col;
  AppStore.typeMenuOpen.value = true;
  AppStore.selectedColumn.value = null;
  AppStore.selectedCell.value = null;
  const rect = event.target.getBoundingClientRect();
  const menuWidth = 140; // Approximate width of type menu
  const margin = 8;
  const windowWidth = window.innerWidth;
  // Clamp x position to keep menu within viewport
  const x = Math.min(rect.left, windowWidth - menuWidth - margin);
  AppStore.typeMenuPos.value = { x, y: rect.bottom + 4 };
}

export function previewTypeConversion(col: string, newType: string) {
  const data = AppStore.currentData.value;
  if (!data || data.length === 0) {
    DialogStore.previewState.title.value = '';
    DialogStore.previewState.stats.value = '';
    DialogStore.previewState.columns.value = [];
    DialogStore.previewState.newColumns.value = [];
    DialogStore.previewState.rows.value = [];
    return;
  }

  // Get current column type
  const model = AppStore.activeModel.value;
  const source = AppStore.activeSource.value;
  let fromType: ColumnType = 'string';

  if (model?.schema) {
    const colInfo = model.schema.find((c: any) => c.name === col);
    if (colInfo) fromType = colInfo.type;
  } else if (source) {
    const colInfo = source.columns.find((c: any) => c.name === col);
    if (colInfo) fromType = colInfo.type;
  }

  // Determine target type
  let toType: ColumnType = newType as ColumnType;
  if (newType === 'auto' && data) {
    const sample = data.slice(0, 50).map((row) => row[col]);
    toType = SchemaEngine.inferType(sample);
  }

  // If same type, clear preview
  if (fromType === toType) {
    DialogStore.previewState.title.value = '';
    DialogStore.previewState.stats.value = '';
    DialogStore.previewState.columns.value = [];
    DialogStore.previewState.newColumns.value = [];
    DialogStore.previewState.rows.value = [];
    return;
  }

  // Get unique values from the column
  // Use a Map to preserve insertion order and track original values
  const uniqueValuesMap = new Map<any, any>();
  for (const row of data) {
    const value = row[col];
    // Use a key that handles objects/errors properly
    const key =
      value === null || value === undefined
        ? '__null__'
        : isConversionError(value)
          ? `__error__${value.message}`
          : JSON.stringify(value);

    if (!uniqueValuesMap.has(key)) {
      uniqueValuesMap.set(key, value);
    }
  }

  const uniqueValues = Array.from(uniqueValuesMap.values());
  const totalRows = data.length;
  const uniqueCount = uniqueValues.length;

  // Convert each unique value
  const previewRows = uniqueValues.map((originalValue) => {
    const convertedValue = convertType(originalValue, fromType, toType);

    // Format the preview row with before and after columns
    const row: any = {
      [`${col} (before)`]: originalValue,
      [`${col} (after)`]: convertedValue,
    };

    // Mark errors
    if (isConversionError(convertedValue)) {
      row._hasError = true;
    }

    return row;
  });

  // Count errors
  const errorCount = previewRows.filter((r) => r._hasError).length;
  const successCount = uniqueCount - errorCount;

  // Set preview state
  DialogStore.previewState.title.value = `Type Conversion: ${col}`;
  DialogStore.previewState.stats.value =
    `<strong>${uniqueCount}</strong> unique values (from ${totalRows} total rows). ` +
    `${successCount} convert successfully, ${errorCount} will produce errors.`;
  DialogStore.previewState.columns.value = [`${col} (before)`, `${col} (after)`];
  DialogStore.previewState.newColumns.value = [`${col} (after)`];
  DialogStore.previewState.rows.value = previewRows;
}

export async function changeColumnType(col: string, newType: string, callbacks: any) {
  AppStore.typeMenuOpen.value = false;

  // Clear preview when applying
  DialogStore.previewState.title.value = '';
  DialogStore.previewState.stats.value = '';
  DialogStore.previewState.columns.value = [];
  DialogStore.previewState.newColumns.value = [];
  DialogStore.previewState.rows.value = [];

  let typeToSet = newType;
  const data = AppStore.currentData.value;
  if (newType === 'auto' && data) {
    const sample = data.slice(0, 50).map((row) => row[col]);
    typeToSet = SchemaEngine.inferType(sample);
  }
  const typeStep = { types: { [col]: typeToSet as ColumnType } };
  await StepService.runTransform('Change Type', typeStep, callbacks);
}

export async function autoDetectSchema(callbacks: any) {
  const data = AppStore.currentData.value;
  const columns = AppStore.columns.value;
  if (!data || !columns) return;
  const types: Record<string, ColumnType> = {};
  columns.forEach((col) => {
    const sample = data!.slice(0, 50).map((row) => row[col]);
    types[col] = SchemaEngine.inferType(sample);
  });
  const typeStep = { types };
  await StepService.runTransform('Auto-Detect Types', typeStep, callbacks);
}

export function clearColumnSelection() {
  AppStore.selectedColumn.value = null;
  AppStore.selectedColumns.value = [];
  AppStore.columnSelectionAnchor.value = null;
  AppStore.selectedCell.value = null;
  AppStore.selectedRows.value = [];
  AppStore.rowSelectionAnchor.value = null;
  AppStore.edaStats.value = null;
  AppStore.edaBrushSelection.value = null;
  AppStore.columnMenuOpen.value = null;
}

/**
 * Clamp `center` to keep a toolbar of `toolbarWidth` within the viewport,
 * with a 12px margin. Returns the clamped x and the arrowOffset (signed
 * distance from the clamped toolbar centre back to the original centre).
 */
export function clampToolbarX(
  center: number,
  toolbarWidth: number
): { x: number; arrowOffset: number } {
  const margin = 12;
  const x = Math.max(
    toolbarWidth / 2 + margin,
    Math.min(window.innerWidth - toolbarWidth / 2 - margin, center)
  );
  return { x, arrowOffset: center - x };
}

export function calculateToolbarPosition(rect: DOMRect, toolbarWidth: number) {
  const center = rect.left + rect.width / 2;
  const { x, arrowOffset } = clampToolbarX(center, toolbarWidth);
  // Position toolbar above the element (top of toolbar at top of element minus spacing)
  // Account for toolbar height (~40px) plus arrow height (6px) plus spacing (8px)
  const toolbarHeight = 40;
  const arrowHeight = 6;
  const spacing = 8;
  const y = rect.top - toolbarHeight - arrowHeight - spacing;
  return { x, y, arrowOffset };
}

/**
 * Position for an EDA chart/stat floating toolbar anchored at `rect.top - 8`.
 * If `eventClientX` is provided and finite, it overrides the rect centre
 * (lets a click on a histogram bar anchor the toolbar at the click point).
 */
export function positionEdaToolbar(
  rect: DOMRect,
  toolbarWidth: number,
  eventClientX?: number
): { x: number; y: number; arrowOffset: number } {
  const center =
    eventClientX != null && Number.isFinite(eventClientX)
      ? eventClientX
      : rect.left + rect.width / 2;
  const { x, arrowOffset } = clampToolbarX(center, toolbarWidth);
  return { x, y: rect.top - 8, arrowOffset };
}

export function updateToolbarPosition() {
  const selectedColumn = AppStore.selectedColumn.value;
  const selectedColumns = AppStore.selectedColumns.value;
  const selectedCell = AppStore.selectedCell.value;

  if (selectedColumns.length > 1) {
    // Multi-column: position above the combined bounding box of all selected headers
    let left = Infinity,
      right = -Infinity,
      top = Infinity,
      bottom = -Infinity;
    for (const col of selectedColumns) {
      const header = document.querySelector(`th[data-col="${col}"]`);
      if (header) {
        const r = header.getBoundingClientRect();
        left = Math.min(left, r.left);
        right = Math.max(right, r.right);
        top = Math.min(top, r.top);
        bottom = Math.max(bottom, r.bottom);
      }
    }
    if (left < Infinity) {
      const combinedRect = {
        left,
        right,
        top,
        bottom,
        width: right - left,
        height: bottom - top,
      } as DOMRect;
      AppStore.columnToolbarPos.value = calculateToolbarPosition(combinedRect, 140);
    }
  } else if (selectedColumn) {
    // Use data-col attribute selector which is more reliable than CSS class
    const header = document.querySelector(`th[data-col="${selectedColumn}"]`);
    if (header) {
      const rect = header.getBoundingClientRect();
      AppStore.columnToolbarPos.value = calculateToolbarPosition(rect, 200);
    }
  }
  if (selectedCell) {
    if (selectedCell.isEda) return;
    // Use data-col and data-row attributes which are more reliable than CSS classes
    const cell = document.querySelector(
      `td[data-col="${selectedCell.col}"][data-row="${selectedCell.rowIdx}"]`
    );
    if (cell) {
      const rect = cell.getBoundingClientRect();
      const toolbarWidth = ['number', 'integer', 'float'].includes(selectedCell.type) ? 220 : 80;
      AppStore.cellToolbarPos.value = calculateToolbarPosition(rect, toolbarWidth);
    }
  }
}

export function selectRow(rowIndex: number, modifiers?: { shift?: boolean; meta?: boolean }) {
  AppStore.selectedColumn.value = null;
  AppStore.selectedColumns.value = [];
  AppStore.columnSelectionAnchor.value = null;
  AppStore.selectedCell.value = null;
  AppStore.typeMenuOpen.value = false;
  AppStore.edaStats.value = null;
  AppStore.edaBrushSelection.value = null;

  if (modifiers?.meta) {
    // Toggle row in selection
    const current = [...AppStore.selectedRows.value];
    const idx = current.indexOf(rowIndex);
    if (idx >= 0) {
      current.splice(idx, 1);
    } else {
      current.push(rowIndex);
    }
    AppStore.selectedRows.value = current;
    AppStore.rowSelectionAnchor.value = rowIndex;
  } else if (modifiers?.shift && AppStore.rowSelectionAnchor.value !== null) {
    // Range selection
    const anchor = AppStore.rowSelectionAnchor.value;
    const min = Math.min(anchor, rowIndex);
    const max = Math.max(anchor, rowIndex);
    AppStore.selectedRows.value = Array.from({ length: max - min + 1 }, (_, i) => min + i);
  } else {
    // Plain click
    AppStore.selectedRows.value = [rowIndex];
    AppStore.rowSelectionAnchor.value = rowIndex;
  }

  requestAnimationFrame(() => updateRowToolbarPosition());
}

export function updateRowToolbarPosition() {
  const selectedRows = AppStore.selectedRows.value;
  if (selectedRows.length === 0) return;

  // Position near the last selected row's gutter cell
  const lastRow = selectedRows[selectedRows.length - 1];
  const pageOffset = (AppStore.currentPage.value - 1) * AppStore.pageSize.value;
  const viewRow = lastRow - pageOffset;

  // Find the gutter cell for this row
  const gutterCells = document.querySelectorAll<HTMLElement>('[data-row-gutter]');
  const cell = gutterCells[viewRow];
  if (cell) {
    const rect = cell.getBoundingClientRect();
    AppStore.rowToolbarPos.value = calculateToolbarPosition(rect, 160);
  }
}

export function selectCell(col: string, value: any, rowIdx: number) {
  AppStore.selectedColumn.value = null;
  AppStore.selectedColumns.value = [];
  AppStore.columnSelectionAnchor.value = null;
  AppStore.selectedRows.value = [];
  AppStore.rowSelectionAnchor.value = null;
  AppStore.typeMenuOpen.value = false;
  let type = 'string';
  const model = AppStore.activeModel.value;
  const source = AppStore.activeSource.value;

  if (model?.schema) {
    const colInfo = model.schema.find((c: any) => c.name === col);
    if (colInfo) type = colInfo.type;
  } else if (source) {
    const colInfo = source.columns.find((c: any) => c.name === col);
    if (colInfo) type = colInfo.type;
  } else {
    type = typeof value === 'number' ? 'number' : 'string';
  }
  const isError = isConversionError(value);
  AppStore.selectedCell.value = { col, value, type, rowIdx, isError };
  requestAnimationFrame(() => updateToolbarPosition());
}

export async function applyQuickCellFilter(op: string, callbacks: any) {
  const selectedCell = AppStore.selectedCell.value;
  if (!selectedCell) return;
  const { col, value, type, isError, isEdaMissing } = selectedCell;
  let expr = '';
  if (isEdaMissing) {
    if (op === 'exact') expr = `[${col}] == null`;
    else if (op === 'not') expr = `[${col}] != null`;
  } else if (isError) {
    if (op === 'exact') expr = `is_error([${col}])`;
    else if (op === 'not') expr = `not is_error([${col}])`;
  } else {
    const formattedValue = HelperHandlers.formatLiteral.call(null as any, value, type);
    if (op === 'exact') expr = `[${col}] == ${formattedValue}`;
    else if (op === 'not') expr = `[${col}] != ${formattedValue}`;
    else if (op === 'gt') expr = `[${col}] > ${formattedValue}`;
    else if (op === 'gte') expr = `[${col}] >= ${formattedValue}`;
    else if (op === 'lt') expr = `[${col}] < ${formattedValue}`;
    else if (op === 'lte') expr = `[${col}] <= ${formattedValue}`;
  }
  if (expr) {
    await StepService.runTransform('Filter', { filter: expr }, callbacks);
  }
  AppStore.selectedCell.value = null;
}

export async function quickSort(order: 'asc' | 'desc', callbacks: any) {
  const selectedColumn = AppStore.selectedColumn.value;
  if (!selectedColumn) return;
  await StepService.runTransform('Sort', { sort: { field: selectedColumn, order } }, callbacks);
  AppStore.selectedColumn.value = null;
}

export function quickFilter(onOpenDialog: (name: string) => void) {
  const selectedColumn = AppStore.selectedColumn.value;
  if (!selectedColumn) return;
  // Expression pre-populated by useDialogState factory reading AppStore.selectedColumn
  onOpenDialog('filter');
}

export async function quickRename(
  prompt: (msg: string, def?: string) => Promise<string | null>,
  alert: (msg: string) => Promise<any>,
  callbacks: any
) {
  const col = AppStore.selectedColumn.value;
  if (!col) return;

  const newName = await prompt(i18n.t('prompts.renameColumn', { ns: 'common', name: col }), col);
  if (!newName || newName.trim() === '') return;

  const trimmedName = newName.trim();
  if (trimmedName === col) return; // No change

  // Check for duplicate column names
  const columns = AppStore.columns.value;
  if (columns.includes(trimmedName)) {
    await alert(i18n.t('validation.duplicate.columnExists', { ns: 'errors', name: trimmedName }));
    return;
  }

  const transform = {
    rename: {
      [col]: trimmedName,
    },
  };

  await StepService.runTransform('Rename Column', transform, callbacks);
  AppStore.selectedColumn.value = null;
}

export async function quickRemove(callbacks: any) {
  const selectedColumn = AppStore.selectedColumn.value;
  if (!selectedColumn) return;
  const col = selectedColumn;
  const confirmed = await NotificationHandlers.confirm.call(
    null as any,
    i18n.t('confirms.removeColumn', { ns: 'common', name: col }),
    undefined,
    i18n.t('buttons.remove', { ns: 'common' })
  );
  if (confirmed) {
    await StepService.runTransform('Remove Column', { remove: [col] }, callbacks);
    AppStore.selectedColumn.value = null;
  }
}

export async function quickRemoveMultiple(callbacks: any) {
  const cols = AppStore.selectedColumns.value;
  if (cols.length === 0) return;
  const confirmed = await NotificationHandlers.confirm.call(
    null as any,
    i18n.t('confirms.removeColumns', { ns: 'common', count: cols.length, names: cols.join(', ') }),
    undefined,
    i18n.t('buttons.remove', { ns: 'common' })
  );
  if (confirmed) {
    await StepService.runTransform('Remove Columns', { remove: [...cols] }, callbacks);
    clearColumnSelection();
  }
}

export function quickDate(onOpenDialog: (name: string) => void) {
  if (!AppStore.selectedColumn.value) return;
  onOpenDialog('date');
}

export function quickReplaceColumn(onOpenDialog: (name: string) => void) {
  if (!AppStore.selectedColumn.value) return;
  // State initialized by useDialogState hook via selectedColumn context
  onOpenDialog('replace');
}

export function quickSpread(onOpenDialog: (name: string) => void) {
  const col = AppStore.selectedColumn.value;
  if (!col) return;
  onOpenDialog('spread');
}

export function openTypeMenuForColumn(col: string) {
  AppStore.typeMenuCol.value = col;
  AppStore.typeMenuOpen.value = true;
  // Position below the column header's type indicator (or header itself)
  const header = document.querySelector(`th[data-col="${col}"]`);
  if (!header) return;
  const indicator = header.querySelector('.type-indicator');
  const el = indicator || header;
  const rect = el.getBoundingClientRect();
  const menuWidth = 140;
  const margin = 8;
  const windowWidth = window.innerWidth;
  const x = Math.min(rect.left, windowWidth - menuWidth - margin);
  AppStore.typeMenuPos.value = { x, y: rect.bottom + 4 };
}

export function quickSplit(onOpenDialog: (name: string) => void) {
  if (!AppStore.selectedColumn.value) return;
  // State initialized by useDialogState hook via selectedColumn context
  onOpenDialog('split');
}

export function quickReplace(onOpenDialog: (name: string) => void) {
  if (!AppStore.selectedCell.value) return;
  // State initialized by useDialogState hook via selectedCell context
  onOpenDialog('replace');
}

export function quickDedupe(onOpenDialog: (name: string) => void) {
  if (!AppStore.selectedColumn.value) return;
  // State initialized by useDialogState hook via selectedColumn context
  onOpenDialog('dedupe');
}

export async function removeSelectedRows(callbacks: any) {
  const indices = AppStore.selectedRows.value;
  if (indices.length === 0) return;
  const confirmed = await NotificationHandlers.confirm.call(
    null as any,
    i18n.t('confirms.removeRows', { ns: 'common', count: indices.length }),
    undefined,
    i18n.t('buttons.remove', { ns: 'common' })
  );
  if (!confirmed) return;
  await StepService.runTransform(
    'Remove Rows',
    { removeRows: { indices: [...indices] } },
    callbacks
  );
  clearColumnSelection();
}

export async function keepSelectedRows(callbacks: any) {
  const indices = AppStore.selectedRows.value;
  if (indices.length === 0) return;
  await StepService.runTransform('Keep Rows', { keepRows: { indices: [...indices] } }, callbacks);
  clearColumnSelection();
}

export async function promoteSelectedRowToHeader(callbacks: any) {
  const indices = AppStore.selectedRows.value;
  if (indices.length !== 1) return;
  const rowIndex = indices[0];
  const confirmed = await NotificationHandlers.confirm.call(
    null as any,
    i18n.t('confirms.promoteRow', { ns: 'common', row: rowIndex + 1 }),
    undefined,
    i18n.t('buttons.promote', { ns: 'common' })
  );
  if (!confirmed) return;
  await StepService.runTransform(
    'Promote Header',
    { promoteHeader: { skipRows: rowIndex } },
    callbacks
  );
  clearColumnSelection();
}

/**
 * Extracts selected rows into a new model.
 * Creates a new model on the same source with the current pipeline + a keepRows step.
 */
export async function extractSelectedRows(switchToModel: (model: any) => void): Promise<void> {
  const indices = AppStore.selectedRows.value;
  if (indices.length === 0) return;

  const activeModel = AppStore.activeModel.value;
  if (!activeModel) return;

  // Verify the model's input exists (source or parent model)
  const hasInput =
    AppStore.sources.value.some((s) => s.id === activeModel.sourceId) ||
    AppStore.models.value.some((m) => m.id === activeModel.sourceId);
  if (!hasInput) return;

  const defaultName = `${activeModel.name}_extract`;
  const modelName = await NotificationHandlers.prompt(
    i18n.t('prompts.extractModel', { ns: 'common' }),
    defaultName
  );
  if (!modelName || modelName.trim() === '') return;
  const name = modelName.trim();

  if (NameService.isModelNameTaken(name, activeModel.sourceId)) {
    await NotificationHandlers.alert(i18n.t('validation.duplicate.modelExists', { ns: 'errors' }));
    return;
  }

  // Copy current steps and append keepRows
  const steps = JSON.parse(JSON.stringify(activeModel.steps));
  steps.push({ keepRows: { indices: [...indices] } });

  const newModel = {
    id: `mdl_${Date.now()}`,
    name,
    sourceId: activeModel.sourceId,
    steps,
    schema: [] as any[],
    data: [] as any[],
    __v: activeModel.__v ?? 1,
  };

  // Compute the full pipeline
  try {
    const context = { sources: AppStore.sources.value, models: AppStore.models.value };
    const result = StepService.computeModelUpToStep(newModel as any, steps.length - 1, context);
    newModel.data = result.data;
    newModel.schema = result.schema as any;
  } catch (error: any) {
    await NotificationHandlers.alert(
      i18n.t('system.extractModelFailed', { ns: 'errors', message: error.message })
    );
    return;
  }

  AppStore.models.value = [...AppStore.models.value, newModel as any];
  switchToModel(newModel);
  clearColumnSelection();

  await PersistenceService.autoSave();
  NotificationHandlers.showSuccess(
    i18n.t('notifications.model.extractCreated', { ns: 'common', name, count: indices.length })
  );
}
